import { html, classes, raw } from '../core/html.js';
import { nav, legalNav, companyNav, site } from '../data/site.js';
import { releasedGames, upcomingGames } from '../data/games.js';
import { t, getLanguage } from '../core/i18n.js';
import { LOCALES } from '../i18n/locales.js';
import { currentRoute } from '../core/router.js';
import { routeHref } from '../core/url.js';
import { icon } from './icon.js';

const pad = (value) => String(value).padStart(2, '0');

export const skipLink = () => html`
  <a class="skip-link" href="#main">${t('a11y.skipToContent')}</a>
`;

/** A section stays active on its own sub-pages: /blog is current on every post. */
const isActive = (active, path) =>
  active === path || (path !== '/' && typeof active === 'string' && active.startsWith(`${path}/`));

export const header = () => {
  const active = currentRoute();

  return html`
    <header class="topbar" id="top">
      <div class="topbar__inner shell">
        <a class="brand" href="${routeHref('/')}" aria-label="${site.name} – ${t('nav.home')}">
          <span class="brand__mark" aria-hidden="true"></span>
          <span class="brand__word">${site.name}</span>
        </a>

        <nav class="topbar__nav" id="site-menu" aria-label="${t('a11y.mainNav')}">
          ${nav.map(
            (item, index) => html`
              <a
                class="${classes('topbar__link', isActive(active, item.path) && 'is-active')}"
                href="${routeHref(item.path)}"
                ${active === item.path ? raw('aria-current="page"') : ''}
              >
                <span class="topbar__index" aria-hidden="true">${pad(index + 1)}</span>
                <span>${t(item.labelKey)}</span>
              </a>
            `,
          )}
          <div class="topbar__nav-foot">
            <a class="topbar__nav-mail" href="mailto:${site.email}">${site.email}</a>
            <nav class="topbar__nav-legal" aria-label="${t('footer.legal')}">
              ${legalNav.map(
                (item) => html`<a href="${routeHref(item.path)}">${t(item.labelKey)}</a>`,
              )}
            </nav>
          </div>
        </nav>

        <div class="topbar__tools">
          <label class="langswitch">
            <span class="visually-hidden">${t('a11y.language')}</span>
            <select class="langswitch__select" data-lang-select>
              ${LOCALES.map(
                (locale) => html`
                  <option value="${routeHref(currentRoute() ?? '/', locale.code)}" ${getLanguage() === locale.code ? 'selected' : ''}>
                    ${locale.label}
                  </option>
                `,
              )}
            </select>
          </label>
          <a class="btn btn--ink btn--sm topbar__cta" href="${routeHref('/contact')}">
            ${t('cta.start')}${icon('arrowRight', { size: 14 })}
          </a>
          <button
            type="button"
            class="topbar__toggle"
            aria-expanded="false"
            aria-controls="site-menu"
            aria-label="${t('nav.menu.open')}"
          >
            <span class="topbar__toggle-open" aria-hidden="true">${icon('menu')}</span>
            <span class="topbar__toggle-close" aria-hidden="true">${icon('close')}</span>
          </button>
        </div>
      </div>
    </header>
  `;
};

export const footer = () => html`
  <footer class="footer">
    <div class="shell">
      <div class="footer__grid">
        <div class="footer__brand">
          <a class="brand" href="${routeHref('/')}" aria-label="${site.name} – ${t('nav.home')}">
            <span class="brand__mark" aria-hidden="true"></span>
            <span class="brand__word">${site.name}</span>
          </a>
          <p class="footer__tagline">${t('footer.tagline')}</p>
          <p class="footer__address">
            ${site.legalName}<br />
            ${site.address.street}<br />
            ${site.address.postalCode} ${site.address.city}
          </p>
          <p class="footer__status">
            ${t('footer.status', { live: pad(releasedGames.length), upcoming: pad(upcomingGames.length) })}
          </p>
        </div>

        <nav class="footer__col" aria-label="${t('a11y.footerNav')}">
          <p class="footer__heading">${t('footer.nav')}</p>
          ${nav.map(
            (item) => html`<a class="footer__link" href="${routeHref(item.path)}">${t(item.labelKey)}</a>`,
          )}
        </nav>

        <nav class="footer__col" aria-label="${t('footer.legal')}">
          <p class="footer__heading">${t('footer.legal')}</p>
          ${legalNav.map(
            (item) => html`<a class="footer__link" href="${routeHref(item.path)}">${t(item.labelKey)}</a>`,
          )}
        </nav>

        <nav class="footer__col" aria-label="${t('footer.company')}">
          <p class="footer__heading">${t('footer.company')}</p>
          ${companyNav.map(
            (item) => html`<a class="footer__link" href="${routeHref(item.path)}">${t(item.labelKey)}</a>`,
          )}
        </nav>

        <div class="footer__col footer__contact">
          <p class="footer__heading">${t('footer.contact')}</p>
          <a class="footer__email" href="mailto:${site.email}">${site.email}</a>
          <a class="link-arrow" href="${routeHref('/contact')}">
            ${t('cta.contact')}${icon('arrowRight', { size: 15 })}
          </a>
        </div>
      </div>

      <div class="footer__word" aria-hidden="true">${site.name}</div>

      <div class="footer__bottom">
        <p>© ${new Date().getFullYear()} ${site.legalName}</p>
        <p>${t('footer.built')}</p>
        <a class="footer__top-link" href="#top">
          ${t('footer.backToTop')}${icon('arrowRight', { size: 14 })}
        </a>
      </div>
    </div>
  </footer>
`;

/** Every page is skip link + header + main + footer; only `body` differs. */
export const page = (body, { modifier = '' } = {}) => html`
  ${skipLink()} ${header()}
  <main id="main" class="${classes('main', modifier)}" tabindex="-1">${body}</main>
  ${footer()}
`;

/**
 * Visible trail that matches the BreadcrumbList in the JSON-LD graph. A page
 * below a section (a blog post) passes that section as `parent` and its own,
 * already translated title as `label`.
 */
export const breadcrumbs = (labelKey, { parent = null, label = null } = {}) => html`
  <nav class="breadcrumbs" aria-label="${t('a11y.breadcrumb')}">
    <a class="link-back" href="${routeHref('/')}">${icon('arrowLeft', { size: 15 })}${t('nav.home')}</a>
    ${parent && html`
      <span class="breadcrumbs__sep" aria-hidden="true">/</span>
      <a class="breadcrumbs__link" href="${routeHref(parent.path)}">${t(parent.labelKey)}</a>
    `}
    <span class="breadcrumbs__sep" aria-hidden="true">/</span>
    <span class="breadcrumbs__current" aria-current="page">${label ?? t(labelKey)}</span>
  </nav>
`;

/** Shown when scripts are blocked; the prerendered page itself stays readable. */
export const noScriptNotice = () => html`
  <p class="noscript">
    ${t('a11y.noScript')}
    <a href="mailto:${site.email}">${site.email}</a>
  </p>
`;
